import { format } from "date-fns";
import { zipSync } from "fflate";
import { supabase } from "@/integrations/supabase/client";
import { renderPdf } from "@/lib/pdf/renderPdf";
import type { PrescriptionReportRow } from "./types";

const slug = (value: string | null | undefined, fallback: string): string => {
  const cleaned = (value ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return cleaned || fallback;
};

/** data-pet-vet-sequencial (sequencial omitido quando não existe); nomes repetidos ganham sufixo. */
export function buildEntryName(row: PrescriptionReportRow, used: Set<string>): string {
  const date = row.issuedAt ? format(new Date(row.issuedAt), "yyyy-MM-dd") : "sem-data";
  const parts = [date, slug(row.petName, "pet"), slug(row.vetName, "vet")];
  if (row.sequence !== null) parts.push(String(row.sequence).padStart(5, "0"));
  const base = parts.join("-");
  let name = `${base}.pdf`;
  for (let n = 2; used.has(name); n += 1) name = `${base}-${n}.pdf`;
  used.add(name);
  return name;
}

async function fetchSignedBytes(path: string, bucket: string): Promise<Uint8Array> {
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 60);
  if (error || !data?.signedUrl) throw error ?? new Error("url_failed");
  const response = await fetch(data.signedUrl);
  if (!response.ok) throw new Error("download_failed");
  return new Uint8Array(await response.arrayBuffer());
}

async function buildRowPdf(row: PrescriptionReportRow, token: string): Promise<Uint8Array> {
  const { data, error } = await supabase
    .from("medical_prescriptions")
    .select("id, signed_storage_path, signed_storage_bucket")
    .eq("id", row.id)
    .single();
  if (error || !data) throw error ?? new Error("prescription_not_found");
  if (data.signed_storage_path) {
    try {
      return await fetchSignedBytes(data.signed_storage_path, data.signed_storage_bucket ?? "signed-pdfs");
    } catch {
      // falls back to the unsigned PDF rendered by the PDF Lambda
    }
  }
  return renderPdf({ resource_type: "prescription", resource_id: row.id, token });
}

export async function buildPrescriptionsZip(
  rows: PrescriptionReportRow[],
  onProgress?: (done: number, total: number) => void,
): Promise<{ bytes: Uint8Array; failed: number }> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error("unauthenticated");
  const used = new Set<string>();
  const files: Record<string, Uint8Array> = {};
  let failed = 0;
  for (let i = 0; i < rows.length; i += 1) {
    try {
      files[buildEntryName(rows[i], used)] = await buildRowPdf(rows[i], token);
    } catch (error) {
      console.error(error);
      failed += 1;
    }
    onProgress?.(i + 1, rows.length);
  }
  return { bytes: zipSync(files, { level: 0 }), failed };
}
